import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { BeneficialOwnershipAPI } from 'api/BeneficialOwnershipAPI';
import CasesAPI from 'api/CasesAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { splitFieldValues } from 'utils/nameMatchScore';
import { OwnershipInvestigationState } from './entityHubSections';
import { fileInvestigationReportToLibrary } from './investigationReportFiling';

const POLL_MS = 15000;
export const INTERRUPTED_AFTER_MS = 3 * 60 * 60 * 1000;

export const TICKET_STATUS = {
  inProgress: 'In Progress',
  complete: 'Complete',
  needsReview: 'Needs Review',
} as const;

export interface OwnershipInvestigation {
  state: OwnershipInvestigationState;
  ticketId: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  finishedHere: boolean;
  /** Whether the ticket holds a report to read, even from a run that did not finish. */
  hasReport: boolean;
  /** Whether the report has been filed to the record's Library. */
  libraryFiled: boolean;
  trialUsed: boolean;
  startError: string | null;
  start: () => Promise<void>;
}

interface InvestigationSubject {
  enabled: boolean;
  name?: string;
  country?: string;
  aliases?: string | string[] | null;
  address?: string | null;
  registrationNumber?: string | null;
  domain?: string | null;
  parentBusiness?: string | null;
  databaseId?: string | number | null;
  databaseEntityId?: string | number | null;
  entityId?: string | number | null;
  /** Called once a finished run's report lands in the record's Library. */
  onReportFiled?: () => void;
}

interface InvestigationTicket {
  _id: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  interruptedAt?: string;
  hasReport?: boolean;
  libraryFiled?: boolean;
}

// Only the full ticket carries the report. The record's lookup reads a
// projection, so a run that did not finish is checked against its full ticket
// (finished runs always open, so only that case needs to know).
const hasReportOf = (row: any): boolean =>
  Boolean(row?.beneficialOwnership || row?.workflowData?.[0]?.result);

const toTicket = (row: any): InvestigationTicket => ({
  _id: row._id,
  status: row.status,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
  interruptedAt: row.interruptedAt,
  hasReport: hasReportOf(row),
  libraryFiled: Boolean(row.libraryFiledAt),
});

const withReportCheck = async (
  row: InvestigationTicket,
): Promise<InvestigationTicket> => {
  const response = await CasesAPI.getCase(row._id);
  return {
    ...row,
    hasReport: response?.success ? hasReportOf(response.result) : false,
  };
};

const isFinished = (status?: string) =>
  status === TICKET_STATUS.complete || status === TICKET_STATUS.needsReview;

const isLost = (ticket: InvestigationTicket) => {
  if (isFinished(ticket.status) || !ticket.createdAt) return false;
  const started = new Date(ticket.createdAt).getTime();
  return (
    Number.isFinite(started) && Date.now() - started > INTERRUPTED_AFTER_MS
  );
};

const ticketState = (
  ticket: InvestigationTicket | null,
): OwnershipInvestigationState => {
  if (!ticket) return 'idle';
  if (ticket.status === TICKET_STATUS.complete) return 'done';
  if (ticket.status === TICKET_STATUS.needsReview) {
    return ticket.interruptedAt ? 'interrupted' : 'needs-review';
  }
  return 'running';
};

const investigationState = ({
  hasAccess,
  loading,
  starting,
  trialUsed,
  ticket,
}: {
  hasAccess: boolean;
  loading: boolean;
  starting: boolean;
  trialUsed: boolean;
  ticket: InvestigationTicket | null;
}): OwnershipInvestigationState => {
  if (!hasAccess) return 'no-access';
  if (loading) return 'loading';
  if (starting) return 'starting';
  const fromTicket = ticketState(ticket);
  return fromTicket === 'idle' && trialUsed ? 'trial-used' : fromTicket;
};

export const splitAliases = (
  aliases: string | string[] | null | undefined,
): string[] => {
  const seen = new Set<string>();
  return splitFieldValues(aliases)
    .map((value) => value.trim())
    .filter((value) => {
      const key = value.toLowerCase();
      if (!value || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const text = (value?: string | null) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const isTrialUsed = async (groupId: string): Promise<boolean> => {
  try {
    const status = await BeneficialOwnershipAPI.getTrialStatus(groupId);
    return Boolean(status?.applicable && status?.used);
  } catch {
    return false;
  }
};

export const useOwnershipInvestigation = (
  subject: InvestigationSubject,
): OwnershipInvestigation => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;
  const userId = cur_user?.basics?.id;
  const userEmail = cur_user?.basics?.email;
  const hasAccess =
    ['business', 'enterprise'].includes(cur_user?.license) ||
    cur_user?.bite_super_admin === true;
  const databaseEntityId =
    subject.databaseEntityId !== undefined && subject.databaseEntityId !== null
      ? String(subject.databaseEntityId)
      : '';
  const active = subject.enabled && hasAccess && Boolean(databaseEntityId);

  const [ticket, setTicket] = useState<InvestigationTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [trialUsed, setTrialUsed] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [finishedHere, setFinishedHere] = useState(false);
  const startingRef = useRef(false);

  const findLatestTicket = useCallback(async () => {
    if (!groupId || !databaseEntityId) return null;
    const response = await CasesAPI.searchCases({
      query: {
        groupId: groupId,
        docType: 'Beneficial Ownership',
        'workflowData.database_entity_id': databaseEntityId,
      },
      skip: 0,
      limit: 1,
      projection: {
        _id: 1,
        status: 1,
        createdAt: 1,
        updatedAt: 1,
        interruptedAt: 1,
        libraryFiledAt: 1,
      },
    });
    if (!response?.success) return undefined;
    const rows = Array.isArray(response.result) ? response.result : [];
    return rows[0] ? toTicket(rows[0]) : null;
  }, [databaseEntityId, groupId]);

  const markInterrupted = useCallback(async (lost: InvestigationTicket) => {
    const interruptedAt = new Date().toISOString();
    await CasesAPI.updateCase({
      _id: lost._id,
      status: TICKET_STATUS.needsReview,
      interruptedAt: interruptedAt,
    });
    return {
      ...lost,
      status: TICKET_STATUS.needsReview,
      updatedAt: interruptedAt,
      interruptedAt: interruptedAt,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTicket(null);
    setFinishedHere(false);
    setStartError(null);
    setTrialUsed(false);
    if (!active) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    Promise.all([
      findLatestTicket(),
      groupId ? isTrialUsed(groupId) : false,
    ]).then(async ([latest, used]) => {
      const marked =
        latest && isLost(latest) ? await markInterrupted(latest) : latest;
      const settled =
        marked && ticketState(marked) === 'interrupted'
          ? await withReportCheck(marked)
          : marked;
      if (cancelled) return;
      if (settled !== undefined) setTicket(settled);
      setTrialUsed(used);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [active, findLatestTicket, groupId, markInterrupted]);

  const runningId = ticket && !isFinished(ticket.status) ? ticket._id : null;
  const runningCreatedAt = runningId ? ticket?.createdAt : undefined;
  useEffect(() => {
    if (!runningId) return undefined;
    const name = subject.name || 'this entity';
    const timer = window.setInterval(async () => {
      const response = await CasesAPI.getCase(runningId);
      const latest = response?.success ? response.result : null;
      if (latest?._id && isFinished(latest.status)) {
        setTicket(toTicket(latest));
        setFinishedHere(true);
        const complete = latest.status === TICKET_STATUS.complete;
        enqueueSnackbar(
          complete
            ? `Beneficial Ownership investigation for ${name} is ready`
            : `The Beneficial Ownership investigation for ${name} finished with problems. Open it to review.`,
          { variant: complete ? 'success' : 'warning' },
        );
        return;
      }
      const inFlight = {
        _id: runningId,
        createdAt: runningCreatedAt,
        hasReport: hasReportOf(latest),
      };
      if (isLost(inFlight)) {
        setTicket(await markInterrupted(inFlight));
        enqueueSnackbar(
          `The Beneficial Ownership investigation for ${name} did not finish. Run it again.`,
          { variant: 'warning' },
        );
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [markInterrupted, subject.name, runningCreatedAt, runningId]);

  // A finished run files its report to the record's Library the first time
  // the record is open after it finishes. A failure stays quiet and is tried
  // again on the next visit; the server keeps it to one copy per run.
  const fileableId =
    ticket &&
    !ticket.libraryFiled &&
    !ticket.interruptedAt &&
    isFinished(ticket.status)
      ? ticket._id
      : null;
  const recordDatabaseId = subject.databaseId ? String(subject.databaseId) : '';
  const onReportFiledRef = useRef(subject.onReportFiled);
  onReportFiledRef.current = subject.onReportFiled;
  const filingAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!fileableId || !recordDatabaseId || !databaseEntityId) return;
    if (filingAttempted.current.has(fileableId)) return;
    filingAttempted.current.add(fileableId);
    fileInvestigationReportToLibrary({
      ticketId: fileableId,
      databaseId: recordDatabaseId,
      databaseEntityId: databaseEntityId,
    })
      .then((outcome) => {
        if (outcome === 'no-report') return;
        setTicket((current) =>
          current?._id === fileableId
            ? { ...current, libraryFiled: true }
            : current,
        );
        if (outcome === 'filed') onReportFiledRef.current?.();
      })
      .catch((error) => {
        console.warn(
          'The Beneficial Ownership report could not be filed to the Library.',
          error,
        );
      });
  }, [databaseEntityId, fileableId, recordDatabaseId]);

  const start = useCallback(async () => {
    if (
      !active ||
      trialUsed ||
      startingRef.current ||
      runningId ||
      !subject.name ||
      !groupId
    ) {
      return;
    }
    startingRef.current = true;
    setStarting(true);
    setStartError(null);
    const owner = {
      kc_id: userId,
      email: userEmail,
      firstName: cur_user?.basics?.firstName,
      lastName: cur_user?.basics?.lastName,
    };
    const ticketName = `Beneficial Ownership - ${subject.name}`;
    const country = subject.country || '';
    const recordLink = {
      database_id: subject.databaseId ? String(subject.databaseId) : '',
      database_entity_id: databaseEntityId,
      entity_id: subject.entityId ? String(subject.entityId) : '',
    };
    const subjectRecord = {
      aliases: splitAliases(subject.aliases),
      address: text(subject.address),
      registration_number: text(subject.registrationNumber),
      domain: text(subject.domain),
      parent_business: text(subject.parentBusiness),
    };
    let ticketId: string | null = null;
    try {
      const created = await CasesAPI.createCase({
        newCase: {
          owner: owner,
          groupId: groupId,
          groupName: cur_user?.selected_team?.name,
          ticketName: ticketName,
          lastUpdate: moment.utc().format('YYYY-MM-DD'),
          activityType: 'workflow',
          workflowType: 'beneficial_ownership',
          docType: 'Beneficial Ownership',
          sharedWith: [],
          status: TICKET_STATUS.inProgress,
          favorite: false,
          classificationValue: subject.name,
          workflowData: [
            {
              entity_name: subject.name,
              country: country,
              second_layer: true,
              source: 'bite_db_record',
              ...recordLink,
              subject_record: subjectRecord,
            },
          ],
          commodity: {
            commodityName: subject.name,
            destinationCountry: country,
          },
          activity: {
            user: owner,
            action: 'started a beneficial ownership investigation from BITE DB',
            timeStamp: Date.now(),
          },
        },
      });
      ticketId = created?.success ? created.result?._id : null;
      if (!ticketId) {
        throw new Error('The investigation could not be saved. Try again.');
      }
      await BeneficialOwnershipAPI.startDetachedRun({
        entity_name: subject.name,
        country: country || undefined,
        group_id: groupId,
        auto_run: true,
        from_entity_record: true,
        subject_record: subjectRecord,
        command: { action: 'run_all' },
        notify_on_complete: true,
        notify: {
          ticket_id: ticketId,
          ticket_name: ticketName,
          recipient_email: userEmail,
          recipient_name: [
            cur_user?.basics?.firstName,
            cur_user?.basics?.lastName,
          ]
            .filter(Boolean)
            .join(' '),
          recipient_kc_id: userId,
          group_id: groupId,
          group_name: cur_user?.selected_team?.name,
          ...recordLink,
        },
      });
      setFinishedHere(false);
      setTicket({
        _id: ticketId,
        status: TICKET_STATUS.inProgress,
        createdAt: new Date().toISOString(),
      });
      enqueueSnackbar(
        `Investigating ${subject.name} in the background. We'll email you when the report is ready.`,
        { variant: 'info' },
      );
    } catch (error: any) {
      if (ticketId) await CasesAPI.deleteCase(ticketId);
      const message =
        error?.message || 'The investigation could not be started. Try again.';
      if (error?.trialUsed) setTrialUsed(true);
      else setStartError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  }, [
    active,
    cur_user,
    databaseEntityId,
    groupId,
    subject.address,
    subject.aliases,
    subject.country,
    subject.databaseId,
    subject.domain,
    subject.entityId,
    subject.name,
    subject.parentBusiness,
    subject.registrationNumber,
    runningId,
    trialUsed,
    userEmail,
    userId,
  ]);

  return {
    state: investigationState({
      hasAccess: hasAccess,
      loading: loading,
      starting: starting,
      trialUsed: trialUsed,
      ticket: ticket,
    }),
    ticketId: ticket?._id || null,
    startedAt: ticket?.createdAt || null,
    finishedAt:
      ticket && isFinished(ticket.status) ? ticket.updatedAt || null : null,
    finishedHere: finishedHere,
    hasReport: Boolean(ticket?.hasReport),
    libraryFiled: Boolean(ticket?.libraryFiled),
    trialUsed: trialUsed,
    startError: startError,
    start: start,
  };
};
